import TopNavigation from "@amzn/awsui-components-react/polaris/top-navigation";
import { TopNavigationProps } from "@amzn/awsui-components-react/polaris/top-navigation/interfaces";
import { useNavigate } from "react-router-dom";
import { HOME_PAGE_URL } from "../module/AuthAdminModule";
import {currentApiDomain, REQUEST_INIT} from "../util/helperUtil";
import locales from "../localization/locales.json"
import i18n from "i18next";
import {getSupportedLanguageOptions, setLanguage} from "../localization/LocalizationModule";
import {useTranslator} from "../localization/Translator";
import { buildAgenticToggleUtility } from "../agentic/components/ToggleButton";
import React, { useEffect } from "react";

interface HeaderLayoutProps {
  userId?: string;
  /**
   * Optional caption rendered as a rounded pill *inside* the Polaris
   * TopNavigation title, immediately after the main "Auth Admin
   * Portal" text. We pass the concatenated title to Polaris (so it
   * renders the whole thing in the right spot, in its own
   * typography), then a `useEffect` splits the rendered title text
   * node into two pieces so only the suffix gets the border-pill
   * styling. No portals, no absolute positioning, no DOM order
   * surprises — the suffix lives inside the same anchor Polaris
   * already laid out.
   */
  subtitle?: string;
  /**
   * Override for the identity (logo) link target. Defaults to
   * `HOME_PAGE_URL` (`/services`) for Classic AAP. The Agentic root
   * layout passes `/services/agentic` so clicking the logo while the
   * user is in Agentic mode keeps them in Agentic mode rather than
   * silently dropping them back into Classic.
   */
  homeHref?: string;
}

interface DropdownEvent {
  detail: {
    id: string;
    [key: string]: any;  // Allows for any additional properties in detail
  };
  [key: string]: any;  // Allows for any additional properties in the event object
}

/** Sentinel marker injected into the title string so the post-render
 *  splitter can find exactly where the suffix begins. Wrapped in
 *  control-character-friendly tokens that no real translation string
 *  would ever contain. */
const PILL_OPEN = "\u2063AGENTIC_PILL_OPEN\u2063";
const PILL_CLOSE = "\u2063AGENTIC_PILL_CLOSE\u2063";


const HeaderLayout = (props: HeaderLayoutProps) => {
  const t = useTranslator();
  const navigate = useNavigate();
  const signOutText = t.translate("auth_admin_portal_sign_out_text");
  const redirectToLogout = () => {
    fetch(currentApiDomain + '/Logout', {
      method: 'GET', ...REQUEST_INIT, headers: {
        Accept: 'application/json', 'Content-Type': 'application/json'
      }
    }).then(response => response.json()).then(function (res) {
      window.location.replace(window.origin + "/logout");
    });
  }
  const userProfileDropDown: TopNavigationProps.Utility = {
    type: "menu-dropdown",
    text: props.userId,
    iconName: "user-profile",
    items: [{ id: "signout", text: signOutText }],
    onItemClick: redirectToLogout
  };
  const localeDropdown : TopNavigationProps.Utility = {
    type: "menu-dropdown",
    // @ts-ignore
    text: locales.supportedLocales[i18n.language].langName,
    items: getSupportedLanguageOptions(),
    // @ts-ignore
    iconName: "flag",
    onItemClick: (event: DropdownEvent) => setLanguage(event.detail.id)
  }
  const agenticToggle = buildAgenticToggleUtility(t.translate, navigate);

  const baseTitle = t.translate("auth_admin_portal_title");
  // Pass the title to Polaris with sentinel markers around the
  // subtitle. After Polaris paints, the effect below replaces the
  // markers with a styled pill <span>.
  const headerTitle = props.subtitle
    ? `${baseTitle} ${PILL_OPEN}${props.subtitle}${PILL_CLOSE}`
    : baseTitle;

  // Post-render splitter — runs after Polaris commits the title text
  // into the DOM. Walks every text node in the document looking for
  // the sentinel pair, and rewrites the parent element's children to
  // be `<title-text>` + `<span class="header-subtitle-pill">` +
  // `<remaining-text>`. Idempotent: if the span is already present we
  // skip.
  useEffect(() => {
    if (!props.subtitle) return undefined;

    const tryInject = (): boolean => {
      const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
      );
      let textNode: Text | null = null;
      while ((textNode = walker.nextNode() as Text | null)) {
        const value = textNode.nodeValue ?? "";
        if (value.indexOf(PILL_OPEN) === -1) continue;
        if (value.indexOf(PILL_CLOSE) === -1) continue;
        const parent = textNode.parentElement;
        if (!parent) continue;
        // Already injected? If the parent already contains a child
        // span with our class, skip so we don't double-wrap.
        if (parent.querySelector(".header-subtitle-pill")) {
          return true;
        }
        const startIdx = value.indexOf(PILL_OPEN);
        const endIdx = value.indexOf(PILL_CLOSE);
        if (startIdx === -1 || endIdx === -1 || endIdx <= startIdx) continue;
        const before = value.slice(0, startIdx);
        const inner = value.slice(startIdx + PILL_OPEN.length, endIdx);
        const after = value.slice(endIdx + PILL_CLOSE.length);

        const beforeNode = document.createTextNode(before);
        const pill = document.createElement("span");
        pill.className = "header-subtitle-pill";
        const dot = document.createElement("span");
        dot.className = "header-subtitle-pill-dot";
        dot.setAttribute("aria-hidden", "true");
        pill.appendChild(dot);
        pill.appendChild(document.createTextNode(inner));
        const afterNode = document.createTextNode(after);

        parent.replaceChild(afterNode, textNode);
        parent.insertBefore(pill, afterNode);
        parent.insertBefore(beforeNode, pill);
        return true;
      }
      return false;
    };

    // First pass synchronously, then a couple of retries in case
    // Polaris paints the title slightly later.
    if (!tryInject()) {
      const tid1 = window.setTimeout(tryInject, 50);
      const tid2 = window.setTimeout(tryInject, 250);
      return () => {
        window.clearTimeout(tid1);
        window.clearTimeout(tid2);
      };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.subtitle, headerTitle]);

  return (
    <TopNavigation
      id="h"
      identity={{
        href: props.homeHref ?? HOME_PAGE_URL,
        title: headerTitle,
      }}
      utilities={[localeDropdown, userProfileDropDown, agenticToggle]}
    />
  );
};
export default HeaderLayout;
